import React from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";

interface DeactivateConfirmDialogProps {
  open: boolean;
  isActive: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DeactivateConfirmDialog: React.FC<DeactivateConfirmDialogProps> = ({
  open,
  isActive,
  isSubmitting = false,
  onClose,
  onConfirm,
}) => {
  const isDeactivating = isActive;

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            border: `1.5px solid ${isDeactivating ? "#EF5350" : "#43A047"}`,
          },
        },
      }}
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}
      >
        {isDeactivating ? (
          <WarningIcon sx={{ color: "#EF5350", fontSize: 26 }} />
        ) : (
          <CheckCircleIcon sx={{ color: "#43A047", fontSize: 26 }} />
        )}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.1rem",
            color: isDeactivating ? "#B71C1C" : "#1B5E20",
          }}
        >
          {isDeactivating ? "Confirm Deactivation" : "Confirm Activation"}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 0.5 }}>
        <Typography sx={{ color: "#424242" }}>
          {isDeactivating
            ? "This will temporarily disable the manager and data will be retained. Confirm?"
            : "This will restore the manager’s access immediately. Please confirm?"}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            textTransform: "none",
            flex: 1,
            borderColor: "#BDBDBD",
            color: "#424242",
            "&:hover": { borderColor: "#9E9E9E" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : undefined
          }
          sx={{
            textTransform: "none",
            flex: 1,
            bgcolor: isDeactivating ? "#C62828" : "#2E7D32",
            "&:hover": { bgcolor: isDeactivating ? "#B71C1C" : "#1B5E20" },
            boxShadow: "none",
          }}
        >
          {isSubmitting
            ? "Please wait..."
            : isDeactivating
              ? "Deactivate"
              : "Activate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeactivateConfirmDialog;
